import {
  Lead,
  GuideSection,
  P,
  UL,
  OL,
  LI,
  Term,
  GuideLink,
  Callout,
  GuideTable,
} from './guide-ui';

/**
 * Body for "DORA logging & monitoring requirements" guide.
 *
 * Legal accuracy notes for future editors:
 * - DORA = Regulation (EU) 2022/2554, in application since 17 January 2025.
 *   Unlike NIS2 it is a Regulation (directly applicable, not transposed).
 * - Detection sits in Article 10; ICT-incident management/reporting in Art. 17.
 * - Reporting timelines come from the incident-reporting RTS/ITS: initial
 *   notification within 4h of classifying an incident as major (and no later
 *   than 24h after detection), intermediate report within 72h, final report
 *   within 1 month. The clock for the 4h starts at CLASSIFICATION.
 * - DORA sets NO single statutory log-retention figure; the ICT risk-management
 *   RTS requires you to define which events are logged, the retention period,
 *   and how logs are secured. Do not invent a fixed number.
 */
export function DoraLoggingGuideBody() {
  return (
    <>
      <Lead>
        DORA turns logging, monitoring and incident reporting into directly-
        applicable EU law for financial entities. You must detect anomalies and
        incidents quickly, keep the evidence, and report major incidents on a
        tight 4-hour / 72-hour / 1-month clock. This guide explains where logging
        fits in DORA, what to capture, how long to keep it, and how the detection
        and reporting duties connect.
      </Lead>

      <Callout title="Not legal advice">
        <p>
          This is general guidance to help you scope your logging and monitoring
          programme. The binding requirements are in Regulation (EU) 2022/2554 and
          its Regulatory and Implementing Technical Standards (RTS/ITS); confirm
          the specifics with your compliance function and competent authority.
        </p>
      </Callout>

      <GuideSection id="what-is-dora" title="What is DORA, and where does logging fit?">
        <P>
          The <Term>Digital Operational Resilience Act (Regulation (EU) 2022/2554)</Term>{' '}
          is the EU&apos;s framework for the digital operational resilience of the
          financial sector. Unlike NIS2, DORA is a <Term>regulation</Term>, which
          applies directly across the EU rather than through national transposition,
          and it has been <Term>in application since 17 January 2025</Term>.
        </P>
        <P>DORA is built on five pillars:</P>
        <UL>
          <LI><Term>ICT risk management</Term>: governance, controls, and protection.</LI>
          <LI><Term>ICT incident management, classification and reporting</Term>.</LI>
          <LI><Term>Digital operational resilience testing</Term>.</LI>
          <LI><Term>ICT third-party risk management</Term>.</LI>
          <LI><Term>Information sharing</Term> on cyber threats.</LI>
        </UL>
        <P>
          Logging and monitoring underpin the first two. You cannot{' '}
          <Term>detect</Term> anomalous activity (Article 10), or{' '}
          <Term>manage and report</Term> an ICT-related incident (Article 17),
          without records of what happened, which makes a credible logging and
          monitoring capability effectively mandatory.
        </P>
      </GuideSection>

      <GuideSection id="who-is-in-scope" title="Who must comply with DORA?">
        <P>
          DORA covers a broad range of <Term>financial entities</Term>, including
          banks and credit institutions, payment and e-money institutions,
          investment firms, insurance and reinsurance undertakings, crypto-asset
          service providers, trading venues, and many others. It also reaches{' '}
          <Term>critical ICT third-party providers</Term> (for example major cloud
          and software vendors), which fall under a dedicated oversight regime.
        </P>
        <P>
          If you supply ICT services to financial entities, expect DORA obligations,
          including monitoring, logging and incident cooperation, to flow down
          to you through contracts.
        </P>
      </GuideSection>

      <GuideSection id="what-to-log" title="What does DORA require you to log and monitor?">
        <P>
          <Term>Article 10 (Detection)</Term> requires financial entities to devote
          sufficient resources to monitor user activity, ICT anomalies and
          ICT-related incidents, particularly cyber-attacks, and to detect them
          promptly. Detection mechanisms must enable multiple layers of control,
          define alert thresholds and criteria that trigger incident-response
          processes, and include automatic alerting for the staff responsible for
          response. In practice, a defensible DORA logging baseline captures:
        </P>
        <UL>
          <LI><Term>User and access activity</Term>: authentication, privileged access, session and account changes.</LI>
          <LI><Term>ICT anomalies and performance issues</Term>: errors, saturation, and signals of single points of failure.</LI>
          <LI><Term>Security events</Term>: IDS/IPS, endpoint, malware and network telemetry, especially around cyber-attacks.</LI>
          <LI><Term>Changes</Term>: configuration, system and control changes across critical or important functions.</LI>
          <LI><Term>Transactions and critical application events</Term> supporting critical or important functions.</LI>
        </UL>
        <P>
          The ICT risk-management RTS expects a <Term>logging policy</Term> that
          identifies the events to be logged, the retention period for logs, and the
          measures used to secure and handle log data.
        </P>
      </GuideSection>

      <GuideSection id="retention" title="How long must you keep logs under DORA?">
        <P>
          DORA does <Term>not</Term> set a single statutory retention period for
          logs. Instead, the technical standards require you to define the retention
          period in your logging policy, justified by the nature of the events and
          your need to reconstruct, investigate and report incidents, and to keep
          logs long enough to support the incident-reporting and resilience-testing
          obligations.
        </P>
        <P>
          Because major-incident final reports and root-cause analysis can run weeks
          after detection, and because financial entities frequently sit under
          overlapping regimes (the GDPR, national financial record-keeping rules,
          and sometimes PCI DSS), most firms land on a multi-month-to-multi-year
          window with a hot tier for active investigation and a cheaper archive tier
          for forensic reconstruction. See the{' '}
          <GuideLink href="/guides/log-retention-requirements">
            log retention requirements by regulation
          </GuideLink>{' '}
          guide for how the different regimes stack up.
        </P>
      </GuideSection>

      <GuideSection id="detection-monitoring" title="Detection and monitoring requirements">
        <P>
          Article 10 makes monitoring an active duty, not passive storage. Your
          monitoring should:
        </P>
        <OL>
          <LI>Continuously monitor user activity, ICT anomalies, and incidents across the estate.</LI>
          <LI>Operate multiple layers of control rather than a single detection point.</LI>
          <LI>Define alert thresholds and criteria that automatically initiate the incident-response process.</LI>
          <LI>Automatically alert the staff responsible for response, with enough context to act.</LI>
          <LI>Help identify material single points of failure.</LI>
        </OL>
        <P>
          This is the functional definition of a SIEM with anomaly detection. DORA
          does not name a product, but meeting continuous monitoring, thresholds,
          and automatic alerting at scale is hard without one.
        </P>
      </GuideSection>

      <GuideSection id="incident-reporting" title="Incident classification and reporting timelines">
        <P>
          DORA (Article 17 and the incident-reporting standards) sets a strict clock
          for <Term>major</Term> ICT-related incidents. An incident is generally
          classified as major when it meets at least two primary criteria, or one
          primary criterion plus the economic-impact threshold. Your logs are the
          evidence base at every stage:
        </P>
        <GuideTable
          head={['Stage', 'Deadline', 'What you submit']}
          rows={[
            ['Initial notification', 'Within 4 hours of classifying the incident as major, and no later than 24 hours after detection', 'First report to your competent authority that a major incident has occurred.'],
            ['Intermediate report', 'Within 72 hours of the initial notification', 'Updated status, including once regular activities are recovered.'],
            ['Final report', 'Within 1 month of the latest intermediate report', 'Root-cause analysis and full impact assessment.'],
          ]}
        />
        <Callout title="The 4-hour clock starts at classification">
          <p>
            The 4-hour deadline runs from the moment you <Term>classify</Term> an
            incident as major, capped at 24 hours after detection. That makes fast
            detection and searchable logs decisive: you cannot classify, let alone
            report, what you cannot see.
          </p>
        </Callout>
      </GuideSection>

      <GuideSection id="protecting-logs" title="Securing and protecting your logs">
        <P>
          DORA&apos;s protection and integrity expectations apply to the log store
          itself, because logs are both evidence and an attacker target:
        </P>
        <UL>
          <LI><Term>Integrity</Term>: tamper-evident, append-only storage so records cannot be silently altered.</LI>
          <LI><Term>Access control</Term>: restrict and log who can read and administer logs.</LI>
          <LI><Term>Availability</Term>: keep logs off-host and centralised so they survive the compromise of the systems that produced them.</LI>
          <LI><Term>Confidentiality and the GDPR</Term>: handle and, where appropriate, redact personal data in logs.</LI>
        </UL>
      </GuideSection>

      <GuideSection id="checklist" title="A practical DORA logging checklist">
        <UL>
          <LI>Centralised collection of user-activity, anomaly, security, change and critical-application logs.</LI>
          <LI>Continuous monitoring with multiple layers of control and defined alert thresholds.</LI>
          <LI>Automatic alerting that initiates the incident-response process.</LI>
          <LI>A written logging policy: which events, the retention period, and how logs are secured.</LI>
          <LI>Tamper-evident, access-controlled, centralised log storage.</LI>
          <LI>Incident classification mapped to DORA&apos;s primary/secondary criteria.</LI>
          <LI>A reporting workflow that can hit the 4h / 72h / 1-month deadlines.</LI>
          <LI>Evidence and forensic capability to support root-cause and final reports.</LI>
        </UL>
      </GuideSection>

      <GuideSection id="how-logpulse-helps" title="How LogPulse supports DORA logging">
        <P>
          LogPulse centralises your logs on one engine and monitors them in real
          time, with search, anomaly detection, and a risk-based{' '}
          <GuideLink href="/siem">Security Monitoring (SIEM)</GuideLink> layer on the
          same data, so continuous detection, thresholds and automatic alerting do
          not require a separate security stack. Detections map to DORA, NIS2 and ISO
          27001 controls, and log data stays in the EU (GDPR-compliant by default).
        </P>
        <P>
          See the{' '}
          <GuideLink href="/compliance/dora">DORA compliance overview</GuideLink>{' '}
          for how this maps to the regulation, and the{' '}
          <GuideLink href="/guides/nis2-logging-requirements">NIS2 logging</GuideLink>{' '}
          guide if you also fall under NIS2. LogPulse supports your compliance
          programme and produces evidence; it is not itself a certification.
        </P>
      </GuideSection>
    </>
  );
}
